/**
 * Utility functions for sharing content to external platforms
 */

export interface ShareData {
  text: string;
  url: string;
  title?: string;
  hashtags?: string[];
}

/**
 * Share to Twitter/X
 */
export function shareToTwitter(data: ShareData): void {
  const { text, url, hashtags = [] } = data;
  const hashtagString = hashtags.length > 0 ? hashtags.map(tag => `#${tag}`).join(' ') : '';
  const twitterText = `${text} ${hashtagString}`.trim();
  
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(url)}`;
  window.open(twitterUrl, '_blank', 'width=550,height=420');
}

/**
 * Share to Facebook
 */
export function shareToFacebook(url: string): void {
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  window.open(facebookUrl, '_blank', 'width=550,height=420');
}

/**
 * Share using native share API (mobile)
 */
export async function shareNative(data: ShareData): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }

  try {
    await navigator.share({
      title: data.title || 'Check this out on Dino',
      text: data.text,
      url: data.url,
    });
    return true;
  } catch (error) {
    // User cancelled or error occurred
    console.error('Native share failed:', error);
    return false;
  }
}

/**
 * Copy link to clipboard
 */
export async function copyToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Share via email
 */
export function shareViaEmail(data: ShareData): void {
  const subject = data.title || 'Check this out on Dino';
  const body = `${data.text}\n\n${data.url}`;
  
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailtoUrl;
}

/**
 * Generate share content for different types
 */
export function generateShareContent(type: string, data: any): ShareData {
  const baseUrl = window.location.origin;
  
  switch (type) {
    case 'review':
      return {
        text: `I just rated "${data.bookTitle}" ⭐️${data.rating}/5 on Dino!`,
        url: `${baseUrl}/book/${data.bookId}?review=${data.reviewId}`,
        title: `My review of ${data.bookTitle}`,
        hashtags: ['BookReview', 'Dino', 'Reading'],
      };
      
    case 'milestone':
      return {
        text: `🎉 I just finished my ${data.count}${getOrdinalSuffix(data.count)} book on Dino!`,
        url: `${baseUrl}/u/${data.username}`,
        title: 'Reading Milestone',
        hashtags: ['ReadingGoals', 'Dino'],
      };
      
    case 'streak':
      return {
        text: `🔥 ${data.days} day reading streak on Dino!`,
        url: `${baseUrl}/u/${data.username}`,
        title: 'Reading Streak',
        hashtags: ['ReadingStreak', 'Dino'],
      };
      
    case 'profile':
      return {
        text: `Check out my reading profile on Dino!`,
        url: `${baseUrl}/u/${data.username}`,
        title: `${data.name}'s Reading Profile`,
        hashtags: ['BookLover', 'Dino'],
      };
      
    case 'book':
      return {
        text: `Reading "${data.title}" - highly recommend! Track your reading on Dino:`,
        url: `${baseUrl}/book/${data.bookId}`,
        title: data.title,
        hashtags: ['BookRecommendation', 'Dino'],
      };
      
    default:
      return {
        text: 'Check this out on Dino!',
        url: baseUrl,
      };
  }
}

/**
 * Helper function to get ordinal suffix
 */
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}
